import React from "react";
import { Skeleton, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

const spinner = (size = 20, color = "#2a3da3") => (
  <LoadingOutlined style={{ fontSize: size, color }} spin />
);

export const TaxInflationInlineLoader = ({
  isLoading,
  text = "Loading...",
  color = "#5f6a8a",
}) => {
  if (!isLoading) return null;

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        color,
        fontWeight: 500,
        background: "#f5f7ff",
        border: "1px solid #e1e7ff",
        borderRadius: "999px",
        padding: "6px 12px",
      }}
    >
      <Spin indicator={spinner(16)} />
      <span>{text}</span>
    </div>
  );
};

export const TaxInflationStatusBanner = ({
  isLoading,
  title = "Loading Tax & Inflation data...",
  description = "Preparing your latest tax and inflation details.",
}) => {
  if (!isLoading) return null;

  return (
    <div
      style={{
        width: "100%",
        borderRadius: "12px",
        border: "1px solid #dbe4ff",
        background: "linear-gradient(180deg, #f7f9ff 0%, #eef3ff 100%)",
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "3px",
        }}
      >
        <div style={{ color: "#213075", fontWeight: 600, fontSize: "13px" }}>
          {title}
        </div>
        <div style={{ color: "#5f6a8a", fontSize: "12px" }}>{description}</div>
      </div>
      <TaxInflationInlineLoader isLoading={true} text="Working..." />
    </div>
  );
};

export const TaxInflationButtonContent = ({
  isLoading,
  idleText,
  loadingText = "Processing...",
}) =>
  isLoading ? (
    <TaxInflationInlineLoader isLoading={true} text={loadingText} />
  ) : (
    idleText
  );

export const TaxSkeletonBox = ({
  width = "100%",
  height = 14,
  radius = 8,
  style = {},
}) => (
  <>
    <style>
      {`@keyframes taxSkeletonShimmer {
          0% { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }`}
    </style>
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background:
          "linear-gradient(90deg, #edf1ff 8%, #f7f9ff 18%, #edf1ff 33%)",
        backgroundSize: "220% 100%",
        animation: "taxSkeletonShimmer 1.5s linear infinite",
        ...style,
      }}
    />
  </>
);

export const TaxInflationHeaderSkeleton = () => (
  <div
    style={{
      width: "100%",
      borderRadius: "12px",
      border: "1px solid #e9edff",
      background: "#ffffff",
      padding: "10px 14px",
      marginBottom: "10px",
    }}
  >
    <Skeleton
      active
      title={{ width: "38%" }}
      paragraph={false}
    />
    <div style={{ marginTop: 10, display: "flex", gap: 12 }}>
      {[1, 2, 3, 4, 5].map((item) => (
        <div
          key={item}
          style={{
            width: 120,
            height: 30,
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <Skeleton active title={false} paragraph={{ rows: 1, width: "100%" }} />
        </div>
      ))}
    </div>
  </div>
);

export const TaxInflationFormSkeleton = ({
  title = "Loading form...",
  rows = 8,
  showButtons = true,
}) => (
  <div style={{ padding: "4px 0 12px 0" }}>
    <Skeleton
      active
      title={{ width: 260 }}
      paragraph={false}
      style={{ marginBottom: 24 }}
    />

    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "18px",
      }}
    >
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index}>
          <Skeleton
            active
            title={{ width: "55%" }}
            paragraph={false}
            style={{ marginBottom: 10 }}
          />
          <Skeleton
            active
            title={{ width: "100%" }}
            paragraph={false}
          />
        </div>
      ))}
    </div>

    {showButtons && (
      <div style={{ marginTop: 26, display: "flex", justifyContent: "space-between" }}>
        <div style={{ width: 120, borderRadius: 999, overflow: "hidden" }}>
          <Skeleton active title={false} paragraph={{ rows: 1, width: "100%" }} />
        </div>
        <div style={{ width: 120, borderRadius: 999, overflow: "hidden" }}>
          <Skeleton active title={false} paragraph={{ rows: 1, width: "100%" }} />
        </div>
      </div>
    )}
    <div style={{ marginTop: 12 }}>
      <TaxInflationInlineLoader isLoading={true} text={title} />
    </div>
  </div>
);

const LineSkeleton = ({ width = "100%", height = 14, radius = 8 }) => (
  <div
    style={{
      width,
      height,
      borderRadius: radius,
      background: "linear-gradient(90deg,#eef2ff 25%,#f7f9ff 37%,#eef2ff 63%)",
      backgroundSize: "400% 100%",
    }}
  />
);

const CardShell = ({ children }) => (
  <div
    style={{
      border: "1px solid #edf0ff",
      borderRadius: 12,
      padding: 14,
      background: "#fff",
    }}
  >
    {children}
  </div>
);

export const TaxInformationSkeleton = () => (
  <div style={{ padding: "8px 0 14px 0" }}>
    <LineSkeleton width={220} height={24} />
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
        gap: 16,
        marginTop: 24,
      }}
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <CardShell key={i}>
          <LineSkeleton width="45%" height={12} />
          <div style={{ marginTop: 10 }}>
            <LineSkeleton width="100%" height={36} />
          </div>
        </CardShell>
      ))}
    </div>
    <div style={{ marginTop: 20 }}>
      <CardShell>
        <LineSkeleton width={180} height={14} />
        <div style={{ marginTop: 10 }}>
          <LineSkeleton width="100%" height={120} />
        </div>
      </CardShell>
    </div>
    <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between" }}>
      <LineSkeleton width={120} height={38} radius={999} />
      <LineSkeleton width={120} height={38} radius={999} />
    </div>
  </div>
);

export const TaxCreditsSkeleton = () => (
  <div style={{ padding: "8px 0 14px 0" }}>
    <LineSkeleton width={180} height={24} />
    <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between" }}>
      <LineSkeleton width={140} height={18} />
      <LineSkeleton width={40} height={40} radius={10} />
    </div>
    <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
      {[1, 2, 3].map((k) => (
        <CardShell key={k}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14 }}>
            <LineSkeleton width="20%" />
            <LineSkeleton width="26%" />
            <LineSkeleton width="22%" />
          </div>
          <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <LineSkeleton width={26} height={26} radius={6} />
            <LineSkeleton width={26} height={26} radius={6} />
          </div>
        </CardShell>
      ))}
    </div>
    <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between" }}>
      <LineSkeleton width={120} height={38} radius={999} />
      <LineSkeleton width={120} height={38} radius={999} />
    </div>
  </div>
);

export const CarryForwardSkeleton = () => (
  <div style={{ padding: "8px 0 14px 0" }}>
    <LineSkeleton width={190} height={24} />
    <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
      {[1, 2].map((k) => (
        <CardShell key={k}>
          <LineSkeleton width="35%" />
          <div style={{ marginTop: 10 }}>
            <LineSkeleton width="100%" height={46} />
          </div>
        </CardShell>
      ))}
    </div>
    <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between" }}>
      <LineSkeleton width={120} height={38} radius={999} />
      <LineSkeleton width={120} height={38} radius={999} />
    </div>
  </div>
);

export const CapitalGainsSkeleton = () => (
  <div style={{ padding: "8px 0 14px 0" }}>
    <LineSkeleton width={290} height={24} />
    <div style={{ marginTop: 20, display: "grid", gap: 12 }}>
      {[1, 2, 3, 4, 5].map((k) => (
        <CardShell key={k}>
          <LineSkeleton width={k % 2 ? "50%" : "75%"} />
          <div style={{ marginTop: 10 }}>
            <LineSkeleton width="100%" />
          </div>
        </CardShell>
      ))}
    </div>
    <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between" }}>
      <LineSkeleton width={120} height={38} radius={999} />
      <LineSkeleton width={120} height={38} radius={999} />
    </div>
  </div>
);

export const InflationRatesSkeleton = () => (
  <div style={{ padding: "8px 0 14px 0" }}>
    <LineSkeleton width={170} height={24} />
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
        gap: 16,
        marginTop: 24,
      }}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <CardShell key={i}>
          <LineSkeleton width="55%" height={12} />
          <div style={{ marginTop: 10 }}>
            <LineSkeleton width="100%" height={36} />
          </div>
        </CardShell>
      ))}
    </div>
    <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between" }}>
      <LineSkeleton width={120} height={38} radius={999} />
      <LineSkeleton width={120} height={38} radius={999} />
    </div>
  </div>
);
